import React, { useState, useEffect, useRef } from 'react';
import { withStyles, Typography, Tooltip, IconButton } from '@material-ui/core';
import { connect } from 'react-redux';
import StorageList from './StorageList';
import { hasPermission, createHistory, hasAtLeastOnePermission, parseURL, formatDate } from '../../sc-utilities';
import { Delete, LibraryBooks, Edit, DeleteForever, CloudDownload } from '@material-ui/icons';
import * as actions from './store/storageActions';
import { Redirect } from 'react-router-dom';
import { calculateDiskSizeInBinary, calculateDiskSizeInDecimal } from '../../sc-product/product.utilities';
import { snackActions } from '../../sc-utilities/SnackBar';
import config from "../../../config/config";
import { download } from "../../sc-utilities/download.utils";
import { getSecurazeSolution, getSecurazeStandard } from '../../sc-dashboard/ProductOverview/ProductsAll';
import { fetchSecurazeSolutions } from '../../sc-admin/SecurazeSolution/store/securazeSolutionActions';

const styles = theme => ({
    root: {
        width: `100%`,
        height: 'auto',
        marginTop: '-25px',
        paddingTop: theme.spacing(),
        [theme.breakpoints.down('md')]: {
            width: '100%',
            padding: '3px',
            marginTop: '-14px'
        },
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='statusActive']": {
            color: theme.palette.success.dark
        },
        "[class*='statusInactive']": {
            color: theme.palette.error.dark
        },
        "[class*='button']": {
            textTransform: 'none',
            textDecoration: 'none'
        },
        "[class*='chip_NotErased']": {
            color: theme.palette.warning.dark,
            padding: 1,
            fontSize: 12
        },
        "[class*='chip_Started']": {
            color: theme.palette.primary.main,
            padding: 1,
            fontSize: 12
        },
        "[class*='chip_InProgress']": {
            color: theme.palette.customInProgress.dark,
            padding: 1,
            fontSize: 12
        },
        "[class*='chip_Error']": {
            color: theme.palette.error.dark,
            padding: 1,
            margin: 1,
            fontSize: 12
        },
        "[class*='chip_Warning']": {
            color: theme.palette.warning.light,
            padding: 1,
            fontSize: 12
        },
        "[class*='chip_Erased']": {
            color: theme.palette.success.dark,
            padding: 1,
            fontSize: 12
        }
    },
    button: {
        textTransform: 'none',
        textDecoration: 'none'
    },
    statusActive: {
        color: theme.palette.success.dark
    },
    statusInactive: {
        color: theme.palette.error.dark
    },
    chip_NotErased: {
        color: theme.palette.warning.dark,
        padding: 1,
        fontSize: 12
    },
    chip_Started: {
        color: theme.palette.primary.main,
        padding: 1,
        fontSize: 12
    },
    chip_InProgress: {
        color: theme.palette.customInProgress.dark,
        padding: 1,
        fontSize: 12
    },
    chip_Error: {
        color: theme.palette.error.dark,
        padding: 1,
        margin: 1,
        fontSize: 12
    },
    chip_Warning: {
        color: theme.palette.warning.light,
        padding: 1,
        fontSize: 12
    },
    chip_Erased: {
        color: theme.palette.success.dark,
        padding: 1,
        fontSize: 12
    },
    chip_Aborted: {
        color: theme.palette.aborted.main,
        padding: 1,
        fontSize: 12
    }
});

const Storage = props => {
    const tableRef = useRef();
    const { classes, translations, permissions, activeCustomerID, onFetchErasureMethods, editable, columns, onGenerateReport, onFetchSecurazeSolutions } = props;
    let visible = columns && columns['Storages'];
    let searchParams = parseURL(window.location.search);
    const [destroyModalState, setDestroyModalState] = useState({ open: false, componentID: null });
    const [erasureMethods, setErasureMethod] = useState({ data: null, loading: false });

    const [securazeSolutionsLookup, setSecurazeSolutionsLookup] = useState({ data: null, loading: false });

    useEffect(() => {

        const fetchErasureMethods = async () => {
            let erasureMethodData = await onFetchErasureMethods({ ActiveCustomerID: activeCustomerID });
            let formattedErasureMethods = erasureMethodData.reduce(
                (obj, item) => Object.assign(obj, { [item.value]: item.displayValue }), {});
            setErasureMethod({ data: formattedErasureMethods, loading: true });
        };
        fetchErasureMethods()
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {

        const fetchSecurazeSolutions = async () => {
            setSecurazeSolutionsLookup({ data: null, loading: true });
            let data = await onFetchSecurazeSolutions({ ActiveCustomerID: activeCustomerID });
            let securazeSolutionsLookup = data?.reduce((acc, item) => {
                acc[item.Key] = item.LongName;
                return acc;
            }, {});
            setSecurazeSolutionsLookup({ data: securazeSolutionsLookup, loading: false });
        };
        hasPermission(permissions, "securaze_solution_list") && fetchSecurazeSolutions();
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, storageComponentID: null, redirectPath: "" });

    const handleDownload = async (rowData, _event) => {
        const objectURL = `${config.backend_domain}:${config.backend_port}/components/wipe-log-download?ComponentID=${rowData.ComponentID}&ActiveCustomerID=${activeCustomerID}`;
        let exportDataSuccessful = await download(objectURL);
        tableRef.current && tableRef.current.onQueryChange();
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
    }

    const tableActionsRow = [
        {
            icon: 'Edit',
            tooltip: translations["edit_storage"] || "Edit storage",
            onClick: (event, rowData) => goToEditStorageProduct(event, rowData),
            hidden: !hasPermission(permissions, "component_load")
        },
        {
            icon: 'LibraryBooks',
            hidden: !hasPermission(permissions, "erasure_report"),
            tooltip: translations["generate_report"] || "Generate report",
            onClick: (event, rowData) => generateReport(event, rowData, translations),
        },
        {
            icon: 'DeleteForever',
            hidden: !hasPermission(permissions, "component_destroy"),
            tooltip: translations["destroy_storage"] || "Destroy storage",
            onClick: (event, rowData) => handleDestroy(rowData, event),
        },
        {
            icon: 'Download',
            hidden: !hasPermission(permissions, "wipe_log_download"),
            tooltip: translations["download_wipe_logs"] || "Wipe log download",
            onClick: (event, rowData) => handleDownload(rowData, event),
        }
    ]

    const getIcon = icon => {
        switch (icon) {
            case 'Edit':
                return <Edit />
            case 'Delete':
                return <Delete />
            case 'LibraryBooks':
                return <LibraryBooks />
            case 'DeleteForever':
                return <DeleteForever />
            case 'Download':
                return <CloudDownload />
            default:
                return <Edit />
        }

    };

    const calculateSizeInDecimal = (size, translations) => {
        return calculateDiskSizeInDecimal(size, translations);
    }

    const calculateSizeInBinary = (size, translations) => {
        return calculateDiskSizeInBinary(size, translations);
    }

    let tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 150, maxWidth: 150 },
            filtering: false,
            render: rowData => {
                return tableActionsRow.map(action => {
                    if (action.hidden) {
                        return null;
                    }
                    if (action.icon === 'DeleteForever' && !(rowData && rowData.StorageComponents && rowData.StorageComponents.length > 0 ? rowData.StorageComponents[0].Type.includes('rom') : false) && (rowData && rowData.StorageComponents && rowData.StorageComponents.length > 0 ? rowData.StorageComponents[0].Shredded : false)) {
                        return null;
                    }
                    return (
                        <Tooltip key={`${action.icon}-${rowData.ComponentID}`} title={action.tooltip}>
                            <IconButton
                                component='div'
                                disabled={action.icon === 'DeleteForever' ? !editable : false}
                                onClick={(event) => action.onClick(event, rowData)}
                            >
                                {
                                    getIcon(action.icon)
                                }
                            </IconButton>
                        </Tooltip>
                    )
                })
            },
        },
        {
            title: translations["type"] || 'Type', field: 'ComponentType',
            hidden: visible ? visible['ComponentType'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ComponentType ? translations[rowData.ComponentType] || "Storage" : translations['short_not_assigned'] || 'n/a'}</Typography>;
            },
        },
        {
            title: translations["report_transport"] || 'Transport', field: 'Transport',
            hidden: visible ? visible['Transport'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData && rowData.StorageComponents && rowData.StorageComponents.length > 0 ? rowData.StorageComponents[0].Transport : translations['short_not_assigned'] || 'n/a'}</Typography>;
            },
        },
        {
            title: translations["size"] || 'Size', field: 'Size',
            hidden: visible ? visible['Size'] : false,
            //Filtering should be implemented with range and converted to bytes
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{calculateSizeInDecimal(rowData?.StorageComponents?.[0]?.Size, translations)}</Typography>;
            },
        },
        {
            title: translations["size_binary"] || 'Size (binary)', field: 'Size',
            hidden: visible ? visible['Size'] : false,
            //Filtering should be implemented with range and converted to bytes
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{calculateSizeInBinary(rowData?.StorageComponents?.[0]?.Size, translations)}</Typography>;
            },
        },
        {
            title: translations["smart_overall"] || 'SMART Overall', field: 'SmartOverall',
            hidden: visible ? visible['SmartOverall'] : false,
            //Multiselect should be implemented with OverallState values
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData && rowData.StorageComponents && rowData.StorageComponents.length > 0 ? rowData.StorageComponents[0].OverallState : translations['short_not_assigned'] || 'n/a'}</Typography>;
            },
        },
        {
            title: translations["health"] || 'Health', field: 'Health',
            hidden: visible ? visible['Health'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData && rowData.StorageComponents && rowData.StorageComponents.length > 0 ? rowData.StorageComponents[0].SmartScore : translations['short_not_assigned'] || 'n/a'}</Typography>;
            },
        },
        {
            title: translations["wipe_status"] || 'Erasure status', field: 'WipeStatus',
            hidden: visible ? visible['WipeStatus'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            lookup: { 'erased': translations['erased'] || 'Erased', 'noterased': translations['not_erased'] || 'Not Erased', 'inprogress': translations['in_progress'] || 'In progress', 'error': translations['error'] || 'Error', 'warning': translations['warning'] || 'Warning', 'aborted': translations['aborted_by_user'] || 'Aborted by user', 'started': translations['started'] || 'Started' },
            defaultFilter: ['erased', 'noterased', 'inprogress', 'error', 'warning', 'aborted', 'started'],
            render: rowData => {
                return <>
                    <CustomChip
                        className={rowData.StorageComponents[0].WipeStatus === 'erased' ? "chip_Erased" :
                            rowData.StorageComponents[0].WipeStatus === 'noterased' ? "chip_NotErased" :
                                rowData.StorageComponents[0].WipeStatus === 'inprogress' ? "chip_InProgress" :
                                    rowData.StorageComponents[0].WipeStatus === 'error' ? "chip_Error" :
                                        rowData.StorageComponents[0].WipeStatus === 'aborted' ? "chip_Aborted" :
                                            rowData.StorageComponents[0].WipeStatus === 'started' ? "chip_Started" : 'chip_Warning'
                        }
                        key={'wipeStatus'}
                        value={rowData.StorageComponents[0].WipeStatus === 'erased' ? translations['erased'] || "Erased" :
                            rowData.StorageComponents[0].WipeStatus === 'noterased' ? translations['not_erased'] || "Not erased" :
                                rowData.StorageComponents[0].WipeStatus === 'inprogress' ? translations['inprogress'] || 'In progress' :
                                    rowData.StorageComponents[0].WipeStatus === 'error' ? translations['error'] || 'Error' :
                                        rowData.StorageComponents[0].WipeStatus === 'aborted' ? translations['aborted_by_user'] || 'Aborted by user' :
                                            rowData.StorageComponents[0].WipeStatus === 'started' ? translations['started'] || 'Started' :
                                                translations['warning'] || "Warning"
                        }
                    />
                </>
            }
        },
        {
            title: translations["wipe_started"] || 'Wipe Started', field: 'WipeStarted',
            hidden: visible ? visible['WipeStarted'] : false,
            width: 100,
            filtering: false,
            filterCellStyle: { minWidth: 150, maxWidth: 300 },
            render: rowData => {
                return <Typography variant={'caption'}>{formatDate(rowData?.StorageComponents?.length > 0 && rowData.StorageComponents[0]?.WipeStarted ? rowData.StorageComponents[0]?.WipeStarted : null)}</Typography>;

            }
        },
        {
            title: translations["wipe_finished"] || 'Wipe finished', field: 'WipeFinished',
            hidden: visible ? visible['WipeFinished'] : false,
            width: 100,
            filtering: false,
            filterCellStyle: { minWidth: 150, maxWidth: 300 },
            render: rowData => {
                return <Typography variant={'caption'}>{formatDate(rowData?.StorageComponents?.length > 0 && rowData.StorageComponents[0]?.WipeFinished ? rowData.StorageComponents[0]?.WipeFinished : null)}</Typography>;

            }
        },
        {
            title: translations["verification_status"] || 'Verification status', field: 'VerificationStatus',
            hidden: visible ? visible['VerificationStatus'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            lookup: { 'verified': translations['verified'] || 'Verified', 'notverified': translations['not_verified'] || 'Not Verified', 'inprogress': translations['in_progress'] || 'In progress', 'error': translations['error'] || 'Error', 'warning': translations['warning'] || 'Warning', 'aborted': translations['aborted_by_user'] || 'Aborted by user', 'started': translations['started'] || 'Started' },
            defaultFilter: ['verified', 'notverified', 'inprogress', 'error', 'warning', 'aborted', 'started'],
            render: rowData => {
                return <>
                    <CustomChip
                        className={rowData.StorageComponents[0].VerificationStatus === 'verified' ? "chip_Erased" :
                            rowData.StorageComponents[0].VerificationStatus === 'notverified' ? "chip_NotErased" :
                                rowData.StorageComponents[0].VerificationStatus === 'inprogress' ? "chip_InProgress" :
                                    rowData.StorageComponents[0].VerificationStatus === 'error' ? "chip_Error" :
                                        rowData.StorageComponents[0].VerificationStatus === 'aborted' ? "chip_Aborted" :
                                            rowData.StorageComponents[0].VerificationStatus === 'started' ? "chip_Started" : 'chip_Warning'
                        }
                        key={'VerificationStatus'}
                        value={rowData.StorageComponents[0].VerificationStatus === 'verified' ? translations['verified'] || "Verified" :
                            rowData.StorageComponents[0].VerificationStatus === 'notverified' ? translations['not_verified'] || "Not verified" :
                                rowData.StorageComponents[0].VerificationStatus === 'inprogress' ? translations['inprogress'] || 'In progress' :
                                    rowData.StorageComponents[0].VerificationStatus === 'error' ? translations['error'] || 'Error' :
                                        rowData.StorageComponents[0].VerificationStatus === 'aborted' ? translations['aborted_by_user'] || 'Aborted by user' :
                                            rowData.StorageComponents[0].VerificationStatus === 'started' ? translations['started'] || 'Started' :
                                                translations['warning'] || "Warning"
                        }
                    />
                </>
            }
        },
        {
            title: translations["verification_started"] || 'Verification Started', field: 'VerificationStarted',
            hidden: visible ? visible['VerificationStarted'] : false,
            width: 100,
            filtering: false,
            filterCellStyle: { minWidth: 150, maxWidth: 300 },
            render: rowData => {
                return <Typography variant={'caption'}>{formatDate(rowData?.StorageComponents?.length > 0 && rowData.StorageComponents[0]?.VerificationStarted ? rowData.StorageComponents[0]?.VerificationStarted : null)}</Typography>;

            }
        },
        {
            title: translations["verification_finished"] || 'Verification finished', field: 'VerificationFinished',
            hidden: visible ? visible['VerificationFinished'] : false,
            width: 100,
            filtering: false,
            filterCellStyle: { minWidth: 150, maxWidth: 300 },
            render: rowData => {
                return <Typography variant={'caption'}>{formatDate(rowData?.StorageComponents?.length > 0 && rowData.StorageComponents[0]?.VerificationFinished ? rowData.StorageComponents[0]?.VerificationFinished : null)}</Typography>;

            }
        },
        {
            title: translations["securaze_standard"] || 'Securaze Standard', field: 'WipeMethod',
            hidden: visible ? visible['WipeMethod'] : false,
            lookup: erasureMethods.data || {},
            cellStyle: { minWidth: 150, maxWidth: 300 },
            render: rowData => {
                return <Typography variant={'caption'}>{getSecurazeStandard(rowData)}</Typography>;

            }
        },
        {
            title: translations["securaze_solution"] || 'Securaze Solution', field: 'SecurazeSolution',
            hidden: visible ? visible['SecurazeSolution'] : false,
            cellStyle: { minWidth: 150, maxWidth: 300 },
            lookup: securazeSolutionsLookup.data || {},
            sorting: false,
            render: rowData => {
                return <Typography variant={'caption'}>{getSecurazeSolution(rowData)}</Typography>;
            }
        },
        {
            title: translations["vendor"] || 'Vendor', field: 'Vendor',
            hidden: visible ? visible['Vendor'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Vendor ? rowData.Vendor : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["model"] || 'Model', field: 'ComponentModel',
            hidden: visible ? visible['ComponentModel'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ComponentModel ? rowData.ComponentModel : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["serial_number"] || 'Serial number', field: 'SerialNumber',
            hidden: visible ? visible['SerialNumber'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.SerialNumber ? rowData.SerialNumber : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["description"] || 'Description', field: 'Description',
            hidden: visible ? visible['Description'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Description ? rowData.Description : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["shredded"] || 'Shredded', field: 'Shredded',
            hidden: visible ? visible['Shredded'] : false,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData && rowData.StorageComponents && rowData.StorageComponents.length > 0 && rowData.StorageComponents[0].Shredded ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        {
            title: translations["deleted"] || 'Deleted', field: 'Deleted',
            hidden: visible ? visible['Deleted'] : false,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0'],
            width: 100,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        }
    ];

    const CustomChip = props => {
        const { keyValue, value, className } = props;
        return (
            <Typography key={keyValue} className={classes[className]}>{value}</Typography>
        );
    };

    const goToEditStorageProduct = (_event, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, ComponentID: rowData.ComponentID, redirectPath: `/storageDetails/edit?ComponentID=${rowData.ComponentID}&&productID=${searchParams.productID}&&editable=${editable ? '1' : '0'}` });
    };

    const generateReport = async (_event, rowData, translations) => {
        await onGenerateReport({ ProductID: rowData.ProductID, ActiveCustomerID: activeCustomerID, ComponentID: rowData.ComponentID }, translations);
    }

    const goToCreateStorages = () => {
        createHistory();
        setRedirectOptions({ redirect: true, StorageID: null, redirectPath: `/storageDetails/create?productID=${searchParams.productID}` });
    };


    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    if (!hasAtLeastOnePermission(permissions, [
        "component_create",
        "component_load",
        "component_delete",
        "component_generate_report"
    ])) {
        tableColumns.shift();
    }

    const handleDestroy = async rowData => {
        if (!rowData) {
            setDestroyModalState({ open: !destroyModalState.open, componentID: null });
            return;
        }
        const componentID = rowData.ComponentID;
        setDestroyModalState({ open: !destroyModalState.open, componentID: componentID });
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'component_list') && erasureMethods.loading ?
                <StorageList editable={editable} destroyModalState={destroyModalState} productID={searchParams.productID} goToCreateStorages={goToCreateStorages} handleDestroy={handleDestroy} tableColumns={tableColumns} tableRef={tableRef} />
                : null}
        </div >
    );
}

const mapStateToProps = state => {
    return {
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchErasureMethods: (user) => (dispatch(actions.fetchErasureMethod(user))),
        onGenerateReport: (component, translations) => dispatch(actions.generateReport(component, translations)),
        onFetchSecurazeSolutions: (data) => dispatch(fetchSecurazeSolutions(data)),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(Storage));